import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import '../css/Navbar.css';
import { parse } from 'tldts';
import { QrCode, X, Copy, Check, Download, Landmark, CreditCard, FileText } from 'lucide-react';

function SbiLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="46" r="40" fill="#0054A6" />
      <circle cx="50" cy="46" r="13" fill="#FFFFFF" />
      <rect x="45.5" y="46" width="9" height="42" fill="#FFFFFF" />
    </svg>
  );
}

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const [applyNowOpen, setApplyNowOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [company, setCompany] = useState({
    name: "Your Bank",
    logo: null,
    favicon: null,
    domain: "",
    subdomain: "",
    superAdminId:"",
  });

  const [fetchError, setFetchError] = useState("");

  const bankDetails = {
    companyName: "AAKIRTI CAPITALS PVT. LTD.",
    bankName: "STATE BANK OF INDIA",
    accountNumber: "43675053320",
    ifscCode: "SBIN0000598",
    panNumber: "ABBCA6537D",
    qrUrl: "/akriti_sbi_qr.jpeg"
  };

  const getDomainInfo = () => {
    const hostname = window.location.hostname;
    const parsed = parse(hostname);
    return {
      domain: parsed.domainWithoutSuffix || "",
      subdomain: parsed.subdomain || "",
    };
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { domain } = getDomainInfo();
        const response = await axios.get("https://payyug.in/api/get-company", { params: { domain } });
        const data = response.data;

        if (data && Object.keys(data).length > 0) {
          setCompany((prev) => ({
            ...prev,
            name: data.name || prev.name,
            logo: data.logo || prev.logo,
            favicon: data.favicon || prev.favicon,
            domain: data.domain || prev.domain,
            subdomain: data.subdomain || prev.subdomain,
            superAdminId: data.superAdminId || prev.superAdminId,
          }));

          if (data.favicon) {
            const link =
              document.querySelector("link[rel*='icon']") ||
              document.createElement("link");
            link.type = "image/png";
            link.rel = "icon";
            link.href = `https://payyug.in/${data.favicon.startsWith("images/") ? data.favicon.slice(7) : data.favicon}`;
            document.head.appendChild(link);
          }
        } else {
          setFetchError("Company info not found.");
        }
      } catch (err) {
        console.error("Company fetch failed:", err);
        setFetchError("Failed to fetch company info.");
      }
    };

    fetchCompany();
  }, []);

  const handleLinkClick = () => {
    if (window.innerWidth <= 960) {
      setMenuOpen(false);
      setLoginDropdownOpen(false);
      setApplyNowOpen(false);
      document
        .querySelectorAll('.hp-navbar-dropdown.hp-navbar-open')
        .forEach((drop) => drop.classList.remove('hp-navbar-open'));
    }
  };

  const handleLinkClickbranch = () => {
    window.location.href = 'https://branch.saberamfi.in';
  };

  const handleKeyDown = (e, setOpen) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpen(prev => !prev);
    }
  };

  const toggleApplyNow = (e) => {
    if (window.innerWidth <= 960) {
      e.preventDefault();
      setApplyNowOpen(prev => !prev);
      e.currentTarget.parentElement.classList.toggle('hp-navbar-open');
    } else {
      setApplyNowOpen(prev => !prev);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return "";
    const cleanPath = path.startsWith("images/") ? path.slice(7) : path;
    return `https://payyug.in/${cleanPath}`;
  };

  const handleCopyDetails = () => {
    const detailsText = `Company Name: ${bankDetails.companyName}\nBank Name: ${bankDetails.bankName}\nAccount Number: ${bankDetails.accountNumber}\nIFSC Code: ${bankDetails.ifscCode}\nPAN Number: ${bankDetails.panNumber}`;
    navigator.clipboard.writeText(detailsText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownloadQR = async () => {
    try {
      const response = await fetch(bankDetails.qrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bankDetails.companyName.replace(/\s+/g, '_')}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading QR Code:", error);
    }
  };

  return (
    <header className={`hp-header ${scrolled ? 'hp-header-scrolled' : ''}`}>
      {/* Logo */}
      <div className="hp-navbar-logo-area">
        {company.logo ? (
          <img src={getImageUrl(company.logo)} alt={company.name || "Bank Logo"} />
        ) : (
          <div className="hp-fallback-logo">{company.name || "Logo"}</div>
        )}
      </div>

      {/* Navigation */}
      <nav className={`hp-navbar-nav-links ${menuOpen ? 'hp-navbar-mobile-open' : ''}`}>
        <Link to="/" onClick={handleLinkClick}>Home</Link>
        <Link to="/about" onClick={handleLinkClick}>About Us</Link>
        <Link to="/service" onClick={handleLinkClick}>Service</Link>
        <Link to="/teams" onClick={handleLinkClick}>Team</Link>
        <Link to="/legal-documents" onClick={handleLinkClick}>Legal Documents</Link>
        <Link to="/contact" onClick={handleLinkClick}>Contact</Link>
      </nav>

      {/* Buttons */}
      <div className="hp-navbar-buttons">
        {/* Pay Now Button */}
        {/* <button 
          type="button" 
          className="hp-navbar-pay-btn"
          onClick={() => setPayModalOpen(true)}
        >
          <QrCode size={16} className="hp-pay-icon" />
          Pay Now
        </button> */}

<button 
  type="button" 
  className="hp-navbar-pay-btn"
  onClick={() => window.open('https://techrupay.com/', '_blank', 'noopener,noreferrer')}
>
  API Panel
  </button>

        {/* Apply Now Dropdown */}
         {/* <div className={`hp-navbar-dropdown ${applyNowOpen ? 'hp-navbar-open' : ''}`}>
          <button
            type="button"
            className="hp-navbar-apply-btn"
            onClick={toggleApplyNow}
            onKeyDown={(e) => handleKeyDown(e, setApplyNowOpen)}
          >
            Apply Loan
          </button>
         {applyNowOpen && (
            <div className="hp-navbar-dropdown-content">
              <Link to="/personal-loan" state={{ company }} onClick={handleLinkClick}>
                Loan Account
              </Link>
            </div>
          )}
        </div>  */}

        <Link
  to="/personal-loan"
  state={{ company }}
  className="hp-navbar-apply-btn"
>
  Apply Loan
</Link>

        {/* <Link
  to="/apply-loan-links"
  state={{ company }}
  className="hp-navbar-apply-btn"
>
  Apply Loan
</Link> */}

        {/* Login Dropdown */}
        <div className={`hp-navbar-dropdown ${loginDropdownOpen ? 'hp-navbar-open' : ''}`}>
          <button
            type="button"
            className="hp-navbar-login-btn"
            onClick={() => setLoginDropdownOpen(!loginDropdownOpen)}
            onKeyDown={(e) => handleKeyDown(e, setLoginDropdownOpen)}
          >
            Login
          </button>
          {loginDropdownOpen && (
            <div className="hp-navbar-dropdown-content">
              <Link to="/login" onClick={handleLinkClick}>Admin Login</Link>
              <Link to="#" onClick={handleLinkClickbranch}>Branch Login</Link>
            </div>
          )}
        </div>
      </div>

      {/* Hamburger */}
      <div
        className={`hp-navbar-hamburger ${menuOpen ? 'hp-navbar-open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMenuOpen(!menuOpen); }}
      >
        <div></div>
        <div></div>
        <div></div>
      </div>

      {fetchError && <div className="hp-navbar-fetch-error">{fetchError}</div>}

      {/* Payment Modal */}
      {payModalOpen && (
        <div className="hp-modal-overlay" onClick={() => setPayModalOpen(false)}>
          <div className="hp-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="hp-modal-close" onClick={() => setPayModalOpen(false)}>
              <X size={20} />
            </button>
            
            <h3 className="hp-modal-title">Company Bank Details</h3>
            
            <div className="hp-modal-body">
              {/* Left Column: QR Area */}
              <div className="hp-modal-qr-section">
                <div className="hp-qr-border">
                  <img src={bankDetails.qrUrl} alt="Payment QR Code" className="hp-qr-image" />
                </div>
                <div className="hp-qr-company-name">{bankDetails.companyName}</div>
              </div>

              {/* Right Column: Text Details */}
              <div className="hp-modal-info-section">
                <div className="hp-info-row">
                  <div className="hp-info-icon-sbi">
                    <SbiLogo />
                  </div>
                  <div>
                    <div className="hp-info-label">Bank Name</div>
                    <div className="hp-info-value bank-highlight">{bankDetails.bankName}</div>
                  </div>
                </div>

                <div className="hp-info-row">
                  <div className="hp-info-icon">
                    <Landmark size={16} color="#000" />
                  </div>
                  <div>
                    <div className="hp-info-label">Account Number</div>
                    <div className="hp-info-value green-text">{bankDetails.accountNumber}</div>
                  </div>
                </div>

                <div className="hp-info-row">
                  <div className="hp-info-icon">
                    <CreditCard size={16} color="#000" />
                  </div>
                  <div>
                    <div className="hp-info-label">IFSC Code</div>
                    <div className="hp-info-value green-text">{bankDetails.ifscCode}</div>
                  </div>
                </div>

                <div className="hp-info-row">
                  <div className="hp-info-icon">
                    <FileText size={16} color="#000" />
                  </div>
                  <div>
                    <div className="hp-info-label">PAN Number</div>
                    <div className="hp-info-value green-text">{bankDetails.panNumber}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Footer Buttons */}
            <div className="hp-modal-footer">
              <button className="hp-btn-copy" onClick={handleCopyDetails}>
                <span>{copied ? "Copied! ✓" : "Copy Account Details"}</span>
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <button className="hp-btn-download" onClick={handleDownloadQR}>
                <span>Download QR Code</span>
                <Download size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;